import { Link } from 'react-router';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Feedback';
import { Photo } from '@/components/ui/Photo';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { DIVISION_PATH, useCategories } from '@/features/catalog/api';
import { useSite } from '@/features/site/SiteProvider';

/** Section C — sports goods: dark band, feature photograph and an editorial category index. */
export function SportsShowcase() {
  const intro = useSite().content.catalogIntro.sports;
  const categories = useCategories('SPORTS');

  return (
    <section aria-labelledby="sports-title" className="section-y bg-primary-dark text-white">
      <div className="container-page grid gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="relative lg:col-span-5">
          <div className="aspect-[4/5] overflow-hidden rounded-[8px] bg-primary lg:sticky lg:top-28">
            <Photo name="ballStitching" sizes="(min-width: 1024px) 40vw, 100vw" position="35% 50%" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <SectionIntro inverted id="sports-title" eyebrow={intro.eyebrow} title={intro.heading} intro={intro.intro} />

          <ol className="mt-12 border-t border-white/12">
            {categories.isPending
              ? [1, 2, 3].map((i) => (
                  <li key={i} className="border-b border-white/12 py-7">
                    <Skeleton className="h-6 w-2/3 bg-white/10" />
                  </li>
                ))
              : categories.data?.map((c, i) => (
                  <li key={c.slug} className="border-b border-white/12">
                    <Link to={`${DIVISION_PATH.SPORTS}/${c.slug}`} className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-x-6 py-7">
                      <span className="font-display text-[13px] font-bold text-steel tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                      <span>
                        <span className="block font-display text-[24px] font-bold tracking-tight transition-colors group-hover:text-accent-soft sm:text-[28px]">
                          {c.name}
                        </span>
                        {c.description && <span className="mt-2 block max-w-[520px] text-[15px] leading-relaxed text-steel">{c.description}</span>}
                      </span>
                      <span className="flex items-center gap-3 text-[13px] text-steel">
                        <span className="hidden sm:inline">
                          {c.productCount} product{c.productCount === 1 ? '' : 's'}
                        </span>
                        <ArrowIcon className="h-5 w-5 text-white" />
                      </span>
                    </Link>
                  </li>
                ))}
          </ol>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <ButtonLink to="/contact#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
              Request a sports goods quote
            </ButtonLink>
            <ButtonLink to={DIVISION_PATH.SPORTS} variant="outlineLight" size="lg">
              View sports catalogue
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>
  );
}
